const CONTEXT_MAP_ERROR = /contextMap\[utilName]/i;

type ObjectProxiable = Record<PropertyKey, unknown>;
type FunctionProxiable = ((...arguments_: unknown[]) => unknown) & Record<PropertyKey, unknown>;
export type Proxiable = ObjectProxiable | FunctionProxiable;

const noopFunction = () => {
  // Intentionally empty.
};

const sharedNoop: FunctionProxiable = Object.assign(noopFunction, {} as Record<PropertyKey, unknown>);

const createNoopProxy = (): Proxiable =>
  new Proxy(sharedNoop, {
    apply: () => {
      // Explicit no-op.
    },
    get: (_target, property) => {
      if (property === 'name') {
        return 'noop';
      }
      if (property === 'length') {
        return 0;
      }
      if (property === Symbol.toStringTag) {
        return 'Function';
      }
      if (property === Symbol.iterator) {
        return;
      }
      return createNoopProxy();
    },
  });

const isProxiable = (value: unknown): value is Proxiable => {
  if (value === null) {
    return false;
  }
  const valueType = typeof value;
  return valueType === 'object' || valueType === 'function';
};

const readProperty = (target: Proxiable, key: PropertyKey): unknown => target[key];

const isCachedProxy = <T extends Proxiable>(candidate: Proxiable | undefined): candidate is T =>
  candidate !== undefined;

const wrapWithGuards = <T extends Proxiable>(value: T, cache: WeakMap<Proxiable, Proxiable>): T => {
  if (!isProxiable(value)) {
    return value;
  }

  const cached = cache.get(value);
  if (isCachedProxy<T>(cached)) {
    return cached;
  }

  const proxy: T = new Proxy(value, {
    get(target, property) {
      if (typeof property === 'symbol') {
        return readProperty(target, property);
      }

      try {
        const result = readProperty(target, property);
        return isProxiable(result) ? wrapWithGuards(result, cache) : result;
      } catch (error) {
        if (error instanceof TypeError && CONTEXT_MAP_ERROR.test(error.message)) {
          return createNoopProxy();
        }
        throw error;
      }
    },
    apply(target, thisArgument, argumentList) {
      try {
        if (typeof target !== 'function') {
          throw new TypeError('Proxiable target is not callable.');
        }
        return target.apply(thisArgument, argumentList);
      } catch (error) {
        if (error instanceof TypeError && CONTEXT_MAP_ERROR.test(error.message)) {
          return;
        }
        throw error;
      }
    },
  });

  cache.set(value, proxy);
  return proxy;
};

// React Dev Overlay (and other inspector proxies) touch arbitrary properties on the
// `trpc.useUtils()` recursive proxy. When they request a helper that doesn’t exist or
// omit the required query args, tRPC throws `contextMap[utilName] is not a function`
// before our app code runs. Wrapping the utils object lets us intercept that specific
// error path and hand the overlay a harmless noop proxy instead, while normal call
// sites still see the real typed helpers.
export function createSafeTrpcUtils<T>(utils: T): T {
  if (!isProxiable(utils)) {
    return utils;
  }
  return wrapWithGuards(utils, new WeakMap<Proxiable, Proxiable>());
}
